import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type {
  CompartilhamentoResumo,
  MensagemCompartilhamento,
  HomeComprador,
  RomaneioRecebido,
  RomaneioPublicoDTO,
  AvisoNovoComprador as AvisoNovoCompradorDTO,
  HomeProdutorDTO,
  CompradorProdutorDTO,
  CompradorDetalheDTO,
  ReceberDTO,
  EventoDaVendaDTO,
} from "@shared/mercado";
import type { ConferenciaDTO } from "@shared/mercado-conferencia";
import { useAuth } from "@/lib/auth-context";
import { useInicioComprador } from "./use-cargas";

/**
 * Mercado C1 — o lado do corretor de um romaneio.
 *
 * ⚠️ Formalizar é ato explícito: `useFormalizarVenda` só é chamado pelo botão do
 * modal. Nenhum `onSuccess` de salvar/emitir venda pode passar a chamá-lo.
 */

const CHAVE = "/api/mercado/compartilhamentos";

/** Todos os compartilhamentos da org — alimenta o chip da lista de romaneios. */
export function useCompartilhamentos() {
  return useQuery<CompartilhamentoResumo[]>({
    queryKey: [CHAVE],
    queryFn: () => fetchJson<CompartilhamentoResumo[]>(CHAVE),
  });
}

/** O compartilhamento de UMA venda (ou nenhum). */
export function useCompartilhamentoDaVenda(vendaId: string | undefined) {
  return useQuery<CompartilhamentoResumo[]>({
    queryKey: [CHAVE, vendaId],
    queryFn: () => fetchJson<CompartilhamentoResumo[]>(`${CHAVE}?vendaId=${vendaId}`),
    enabled: !!vendaId,
  });
}

/** Prévia do texto antes de formalizar. Só busca quando o modal está aberto.
 *  Mesmo formato do envio — prévia não é contrato paralelo. */
export function usePreviaMensagem(
  vendaId: string | undefined,
  vencimento: string,
  telefone: string,
  ativo: boolean,
) {
  const params = new URLSearchParams({ vendaId: vendaId ?? "" });
  if (vencimento) params.set("vencimento", vencimento);
  if (telefone) params.set("telefone", telefone);
  const url = `${CHAVE}/previa?${params.toString()}`;
  return useQuery<MensagemCompartilhamento>({
    queryKey: [CHAVE, "previa", vendaId, vencimento, telefone],
    queryFn: () => fetchJson<MensagemCompartilhamento>(url),
    enabled: ativo && !!vendaId,
  });
}

export function useMensagemCompartilhamento(id: string | undefined) {
  return useQuery<MensagemCompartilhamento>({
    queryKey: [CHAVE, id, "mensagem"],
    queryFn: () => fetchJson<MensagemCompartilhamento>(`${CHAVE}/${id}/mensagens`),
    enabled: !!id,
  });
}

function invalidarMercado(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [CHAVE] });
  // Formalizar/enviar muda o estado do corretor e as pendências da home.
  qc.invalidateQueries({ queryKey: [CHAVE_PRODUTOR] });
}

export function useFormalizarVenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { vendaId: string; telefone: string; vencimento: string | null }) =>
      apiRequest("POST", CHAVE, dados).then(r => r.json()) as Promise<CompartilhamentoResumo>,
    onSuccess: () => invalidarMercado(qc),
  });
}

/** Muda a venda do Cacho junto (status, número, "A receber"): lista, detalhe e topo. */
function invalidarVendas(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["/api/vendas"] });
  qc.invalidateQueries({ queryKey: ["/api/vendas/resumo"] });
}

/**
 * "Não recebi" — o produtor contesta o pagamento que o COMPRADOR registrou. O
 * romaneio fica CONTESTADO e a venda volta a EMITIDA, numa transação no servidor.
 */
export function useContestarPagamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { lancamentoId: string; motivo: string }) =>
      apiRequest("POST", `${CHAVE}/pagamentos/${dados.lancamentoId}/contestar`, { motivo: dados.motivo })
        .then(r => r.json()) as Promise<{ status: string; vendaVoltouAEmitida: boolean }>,
    onSuccess: () => {
      invalidarMercado(qc);
      invalidarVendas(qc);
    },
  });
}

/** A venda nasceu de um registro do comprador (adoção pelo `/p/`)? */
export function useOrigemDaVenda(vendaId: string | undefined, ativo: boolean) {
  return useQuery<{ adotada: boolean; compradorNoRizoma: boolean }>({
    queryKey: [CHAVE, "origem", vendaId],
    queryFn: () => fetchJson(`${CHAVE}/origem?vendaId=${vendaId}`),
    enabled: !!vendaId && ativo,
  });
}

/** "Conferência de {comprador}" na venda — leitura, e só. */
export function useConferenciaDaVenda(compartilhamentoId: string | undefined) {
  return useQuery<{ compradorNome: string | null; conferencia: ConferenciaDTO | null }>({
    queryKey: [CHAVE, "conferencia", compartilhamentoId],
    queryFn: () => fetchJson(`${CHAVE}/${compartilhamentoId}/conferencia`),
    enabled: !!compartilhamentoId,
  });
}

/**
 * O histórico da venda (produtor): vencimento e correções, com quem e quando.
 * Frases prontas do servidor. Sob a chave do compartilhamento: trocar o
 * vencimento ou aplicar a conferência invalida `[CHAVE]` e isto recarrega junto.
 */
export function useHistoricoDaVenda(compartilhamentoId: string | undefined) {
  return useQuery<EventoDaVendaDTO[]>({
    queryKey: [CHAVE, "historico", compartilhamentoId],
    queryFn: () => fetchJson(`${CHAVE}/${compartilhamentoId}/historico`),
    enabled: !!compartilhamentoId,
  });
}

/**
 * "Aplicar os números de {comprador}" — kg e R$/kg da conferência vão para a
 * venda; o servidor recalcula o total e a conferência continua valendo. Muda a
 * venda (itens, total, A receber) e o Mercado (conferência, divergência).
 */
export function useAplicarNumerosDaConferencia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vendaId: string) =>
      apiRequest("POST", `/api/vendas/${vendaId}/aplicar-conferencia`, {})
        .then(r => r.json()) as Promise<{ ok: true; valorAntes: number; valorDepois: number }>,
    onSuccess: () => {
      invalidarMercado(qc);
      invalidarVendas(qc);
    },
  });
}

/**
 * "Emitir e confirmar com {comprador}" — a venda que ELE registrou é emitida e
 * formalizada com ele numa transação só. O vencimento só vai quando o registro
 * não trouxe um (o servidor recusa emitir em aberto sem prazo).
 */
export function useEmitirEConfirmar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { vendaId: string; vencimento: string | null }) =>
      apiRequest("POST", `${CHAVE}/emitir-e-confirmar`, dados)
        .then(r => r.json()) as Promise<{ numero: string; compartilhamentoId: string }>,
    onSuccess: () => {
      invalidarMercado(qc);
      invalidarVendas(qc);
    },
  });
}

export function useMarcarEnviado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `${CHAVE}/${id}/enviado`).then(r => r.json()),
    onSuccess: () => invalidarMercado(qc),
  });
}

/** Desfaz o "marcar como enviado" — o toque marca junto com abrir o WhatsApp, e
 *  desistir na tela do WhatsApp é um caso real. */
export function useDesfazerEnvio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `${CHAVE}/${id}/enviado/desfazer`).then(r => r.json()),
    onSuccess: () => invalidarMercado(qc),
  });
}

export function useRevogarCompartilhamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `${CHAVE}/${id}/revogar`).then(r => r.json()),
    onSuccess: () => invalidarMercado(qc),
  });
}

// ─── C2 — conta do corretor ───────────────────────────────────────────────────

/** Home do corretor. O KPI principal é adaptativo — ver HomeComprador. */
export function useHomeComprador(ativo: boolean) {
  return useQuery<HomeComprador>({
    queryKey: ["/api/mercado/comprador/home"],
    queryFn: () => fetchJson<HomeComprador>("/api/mercado/comprador/home"),
    enabled: ativo,
  });
}

// ─── Visão do PRODUTOR (PR 1 da UI: Início · Corretores · A receber) ─────────

/**
 * Prefixo ÚNICO das queries do produtor. Toda mutação que mexe em romaneio,
 * comprador ou recebimento invalida por este prefixo — Início, Corretores, o
 * detalhe e A receber somam sobre a mesma base e precisam recarregar juntos.
 */
export const CHAVE_PRODUTOR = "/api/mercado/produtor";

export function useHomeProdutor(ativo: boolean) {
  return useQuery<HomeProdutorDTO>({
    queryKey: [CHAVE_PRODUTOR, "home"],
    queryFn: () => fetchJson<HomeProdutorDTO>(`${CHAVE_PRODUTOR}/home`),
    enabled: ativo,
  });
}

export interface FiltroCorretores {
  estado?: "todos" | "ativos" | "sem_acesso";
  q?: string;
}

export function useCompradoresProdutor(filtro: FiltroCorretores, ativo: boolean) {
  const params = new URLSearchParams();
  if (filtro.estado && filtro.estado !== "todos") params.set("estado", filtro.estado);
  if (filtro.q) params.set("q", filtro.q);
  const qs = params.toString();
  return useQuery<CompradorProdutorDTO[]>({
    queryKey: [CHAVE_PRODUTOR, "corretores", filtro.estado ?? "todos", filtro.q ?? ""],
    queryFn: () => fetchJson<CompradorProdutorDTO[]>(`${CHAVE_PRODUTOR}/corretores${qs ? `?${qs}` : ""}`),
    enabled: ativo,
  });
}

export interface FiltroDetalheCorretor {
  ano?: number;
  pagamento?: "aberto" | "pagos" | "todos";
}

export function useCompradorDetalhe(compradorId: string | undefined, filtro: FiltroDetalheCorretor) {
  const params = new URLSearchParams();
  if (filtro.ano) params.set("ano", String(filtro.ano));
  if (filtro.pagamento && filtro.pagamento !== "todos") params.set("pagamento", filtro.pagamento);
  const qs = params.toString();
  return useQuery<CompradorDetalheDTO>({
    queryKey: [CHAVE_PRODUTOR, "corretores", compradorId, filtro.ano ?? 0, filtro.pagamento ?? "todos"],
    queryFn: () => fetchJson<CompradorDetalheDTO>(`${CHAVE_PRODUTOR}/corretores/${compradorId}${qs ? `?${qs}` : ""}`),
    enabled: !!compradorId,
  });
}

export function useReceber(ativo: boolean) {
  return useQuery<ReceberDTO>({
    queryKey: [CHAVE_PRODUTOR, "receber"],
    queryFn: () => fetchJson<ReceberDTO>(`${CHAVE_PRODUTOR}/receber`),
    enabled: ativo,
  });
}

/**
 * "Recebi": marca a venda como paga pelo caminho do Cacho e recomputa o status do
 * compartilhamento. Invalida o produtor inteiro E as vendas do Cacho — a venda
 * mudou de status lá também.
 */
export function useRecebi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { vendaId: string; data?: string }) =>
      apiRequest("POST", `${CHAVE_PRODUTOR}/receber/${dados.vendaId}/recebi`, { data: dados.data })
        .then(r => r.json()) as Promise<{ ok: true; jaEstavaPago: boolean; statusPagamento: "PAGO" | "A_PAGAR" | null }>,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_PRODUTOR] });
      qc.invalidateQueries({ queryKey: [CHAVE] });
      qc.invalidateQueries({ queryKey: ["/api/vendas"] });
    },
  });
}

/**
 * Badges do submenu e da tabbar. Reusa a query da home (mesma chave) — não é um
 * segundo request. Até o C4, "A receber" conta os romaneios que vencem em 7 dias;
 * "Ofertas" só ganha número no PR 3.
 */
export function useBadgesMercado(): { receber: number; ofertas: number; romaneios: number } {
  const { user, modulesEnabled } = useAuth();
  const temMercado = modulesEnabled.indexOf("mercado") >= 0;
  const ehComprador = user?.orgTipo === "COMPRADOR";
  const { data } = useHomeProdutor(!ehComprador && temMercado);
  // O badge do comprador sai do PAINEL, que a tela de Início já carregou: uma
  // consulta própria só para o número pediria a mesma conta duas vezes e podia
  // mostrar um badge de um instante e uma lista de outro.
  const { data: painel } = useInicioComprador(ehComprador && temMercado);
  return {
    receber: data?.receber.venceSemana?.qtd ?? 0,
    ofertas: 0,
    romaneios: painel?.kpis.romaneios.aConferir ?? 0,
  };
}

/**
 * "Fulano criou acesso e agora acompanha suas cargas."
 *
 * Também é o controle compensatório do claim-by-token (o token prova posse do link,
 * não do número) — quem reconhece o nome, ou estranha, é o produtor.
 */
export function useAvisosNovoCorretor(ativo: boolean) {
  return useQuery<AvisoNovoCompradorDTO[]>({
    queryKey: ["/api/mercado/produtor/avisos"],
    queryFn: () => fetchJson<AvisoNovoCompradorDTO[]>("/api/mercado/produtor/avisos"),
    enabled: ativo,
  });
}

export function useMarcarAvisoVisto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vinculoId: string) =>
      apiRequest("POST", `/api/mercado/produtor/avisos/${vinculoId}/visto`).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/mercado/produtor/avisos"] }),
  });
}

// ─── C4: confirmar e contestar SAÍRAM ────────────────────────────────────────
//
// `useConfirmarPagamento` e `useContestarPagamento` chamavam
// `/api/mercado/produtor/pagamentos/:id/…`, que não existem mais: com "quem
// registra quita" o lançamento nasce confirmado e não há segundo ato.
